/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { Query_Keys } from "../../../lib/queryKeys";
import { mergeQueryKeys, getBaseQueryKey } from "../utils/queryKeyHelpers";
import { billingReportRefreshKeys, subGroupDependentRefreshKeys } from "./sharedRefreshKeys";
import { restaurantMutationKeyGroups } from "./restaurantMutationKeys";

const restaurantGroupBaseKeys = mergeQueryKeys(
  getBaseQueryKey(Query_Keys.restaurant_groups),
  getBaseQueryKey(Query_Keys.restaurant_groups_dropdown),
  getBaseQueryKey(Query_Keys.permission_restaurant_group),
);

// Restaurant sub group mutations also need to invalidate the sub group dropdown and
// individual restaurant caches (a restaurant's sub group membership can change), without
// forcing plain restaurant-group mutations to invalidate those unrelated caches too.
const restaurantSubGroupBaseKeys = mergeQueryKeys(
  restaurantGroupBaseKeys,
  getBaseQueryKey(Query_Keys.restaurant_sub_groups_dropdown),
  getBaseQueryKey(Query_Keys.show_restaurant),
);

const restaurantSubGroupDeleteKeys = mergeQueryKeys(
  restaurantSubGroupBaseKeys,
  subGroupDependentRefreshKeys,
);

// Industry cards show the issuing and audience group names, so a rename or delete refreshes them.
const restaurantGroupChangeKeys = mergeQueryKeys(
  restaurantGroupBaseKeys,
  getBaseQueryKey(Query_Keys.industry_discount_cards),
  getBaseQueryKey(Query_Keys.eligible_industry_discount_cards),
);

// The benefit program form picks its partnership from active_partners_list, which
// lists ACTIVE partnerships only — so a create adds a row to it, and an update
// (renamed, activated, deactivated) or a delete changes or removes one.
const restaurantGroupPartnershipBaseKeys = mergeQueryKeys(
  getBaseQueryKey(Query_Keys.restaurant_groups),
  getBaseQueryKey(Query_Keys.restaurant_group_partnership),
  getBaseQueryKey(Query_Keys.active_partners_dropdown),
);

const restaurantGroupPartnershipRestaurantKeys = mergeQueryKeys(
  restaurantGroupPartnershipBaseKeys,
  restaurantMutationKeyGroups.create,
);

// A group's name and cutoff date shape its Billing Report rows and orders. A new group has no restaurants, so no row: create skips it.
export const restaurantGroupMutationKeyGroups = {
  create: restaurantGroupBaseKeys,
  update: mergeQueryKeys(restaurantGroupChangeKeys, billingReportRefreshKeys),
  delete: mergeQueryKeys(restaurantGroupChangeKeys, billingReportRefreshKeys),
  subGroupCreate: restaurantSubGroupBaseKeys,
  subGroupUpdate: restaurantSubGroupBaseKeys,
  subGroupDelete: restaurantSubGroupDeleteKeys,
  partnershipCreate: restaurantGroupPartnershipBaseKeys,
  partnershipBusinessPartnerCreate: restaurantGroupPartnershipRestaurantKeys,
  partnershipUpdate: restaurantGroupPartnershipBaseKeys,
  partnershipDelete: restaurantGroupPartnershipBaseKeys,
};
